<template>
  <SimpleDataWithVariation
    :title="title"
    :item-id="itemId"
    :value="value"
    :variation="variation"
    :raw-variation="rawVariation"
    :request-status="requestStatus"
    :comparation-value="comparationValue"
    v-bind="$attrs"
    class="make-a-copy"
    @reload="getData"
  />
</template>

<script>
import { mapActions } from 'vuex';
import { formatSocialNumberWithDecimal } from '@/utils/formatter';
import { DATA_TYPE } from '@/store/modules/cards/constants';
import SimpleDataWithVariation from './models/SimpleDataWithVariation.vue';
import sacListeningModel from '@/mixins/sac-listening-model';

export default {
  name: 'PostSamplingProjectionSacListening',
  components: {
    SimpleDataWithVariation,
  },
  mixins: [
    sacListeningModel,
  ],
  props: {
    title: {
      type: String,
      required: true,
    },
    itemId: {
      type: [String, Number],
      required: true,
    },
  },
  data() {
    return {
      requestStatus: 'loading',
      showGlobalFilterModal: false,
      value: '',
      variation: '',
      rawVariation: '',
      comparationValue: '',
    };
  },
  computed: {
    filterButton() {
      return this.$t('components.OperatorsLogs.filterLabel');
    },
  },
  created() {
    this.getData();
  },
  methods: {
    ...mapActions('cards', [
      'getSacListeningWarroomService',
    ]),
    getData(options) {
      if (!options?.silentRefresh) {
        this.requestStatus = 'loading';
      }
      const serviceConfigs = [
        { filterType: DATA_TYPE.LISTENING, serviceParams: { favorite: true } },
        { filterType: DATA_TYPE.SAC, serviceParams: { favorite: true } },
        { filterType: DATA_TYPE.LISTENING, serviceParams: { noFilter: true } },
        { filterType: DATA_TYPE.SAC, serviceParams: { noFilter: true } },
        { filterType: DATA_TYPE.LISTENING, serviceParams: { totalFavorite: true } },
        { filterType: DATA_TYPE.SAC, serviceParams: { totalFavorite: true } },
      ];
      const promises = serviceConfigs.map(cfg =>
        this.getSacListeningWarroomService({
          itemId: this.itemId,
          servicePath: 'monitoringOverview',
          filterType: cfg.filterType,
          serviceParams: cfg.serviceParams,
        })
      );
      Promise.all(
        promises
      ).then(([responseListeningFavorite, responseSacFavorite, responseListeningTotalNoFilter, responseSacTotalNoFilter, responseListeningTotalFavorite, responseSacTotalFavorite]) => {
        const projectionListening = this.calculateProjection(responseListeningFavorite.collected_mentions, responseListeningTotalNoFilter.collected_mentions, responseListeningTotalFavorite.collected_mentions);
        const projectionSac = this.calculateProjection(responseSacFavorite.collected_mentions, responseSacTotalNoFilter.collected_mentions, responseSacTotalFavorite.collected_mentions);
        this.value = projectionListening + projectionSac;
        const projectionListeningVariation = this.calculateProjection(
          responseListeningFavorite.collected_mentions_variation,
          responseListeningTotalNoFilter.collected_mentions_variation,
          responseListeningTotalFavorite.collected_mentions_variation
        );

        const projectionSacVariation = this.calculateProjection(
          responseSacFavorite.collected_mentions_variation,
          responseSacTotalNoFilter.collected_mentions_variation,
          responseSacTotalFavorite.collected_mentions_variation
        );

        this.variation = projectionListeningVariation + projectionSacVariation;
        this.rawVariation = projectionListeningVariation + projectionSacVariation;
        const previousProjectionListening = this.calculateProjection(responseListeningFavorite.previous_collected_mentions, responseListeningTotalNoFilter.previous_collected_mentions, responseListeningTotalFavorite.previous_collected_mentions);
        const previousProjectionSac = this.calculateProjection(responseSacFavorite.previous_collected_mentions, responseSacTotalNoFilter.previous_collected_mentions, responseSacTotalFavorite.previous_collected_mentions);
        this.comparationValue = formatSocialNumberWithDecimal(previousProjectionListening + previousProjectionSac);
        this.requestStatus = 'success';
      }).catch((error) => {
        console.error('Error fetching data for PostSamplingProjectionSacListening:', error);
        this.requestStatus = 'error';
      });
    },
    /**
     * Calculates the projected value of SAC listening posts based on favorite (checked) and total counts.
     *
     * The projection formula estimates the expected number of posts in the full dataset
     * that would be "favorite" (checked) (i.e., meet a certain criteria), based on the observed ratio
     * in a filtered subset.
     *
     * @param {number} responseFavorite - The number of "favorite" (checked) posts in the filtered subset.
     * @param {number} responseTotalNoFilter - The total number of posts in the unfiltered (full) dataset.
     * @param {number} responseTotalFavorite - The total number of posts in the filtered subset.
     * @returns {number} The projected number of "favorite" (checked) posts in the full dataset.
     *
     * Formula:
     *   projection = responseTotalNoFilter * (responseFavorite / responseTotalFavorite)
     * Where (responseFavorite / responseTotalFavorite) is the observed ratio in the filtered subset.
     */
    calculateProjection(responseFavorite, responseTotalNoFilter, responseTotalFavorite) {
      if (
        responseTotalFavorite === undefined ||
        responseTotalFavorite === null ||
        responseTotalFavorite === 0
      ) {
        return 0;
      }
      const postsCheckedDivision = responseFavorite / responseTotalFavorite;
      const result = responseTotalNoFilter * postsCheckedDivision;
      return result;
    },
  },
};
</script>

<style scoped lang="scss">
</style>
